import { Check, ChevronDown, HardDrive, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog.js';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.js';
import { Input } from '../../../components/ui/input.js';
import { Label } from '../../../components/ui/label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select.js';
import { cn } from '../../../lib/utils.js';
import { useAuth } from '../../../lib/auth-context.js';
import { useLicense } from '../../common/license-context.js';
import { ErdGateway } from '../erd-gateway.js';
import type { PlanningRecordType, PlanningWorkspace } from '../erd-bridge.js';
import { useTriggerPlanningBackup } from '../hooks.js';

/**
 * Modal for triggering a Planning backup. Pre-2026-05-26 this was a
 * stack of free-text inputs — the operator had to know UUIDs by
 * heart. Now:
 *
 *   - **Planning instance**: `Select` populated from the license's
 *     primary + secondary workfront-configs (the engine pipeline
 *     matches by `workfront_configs.name` OR UUID since #157).
 *   - **Workspaces**: multi-select dropdown, fetched on dialog open
 *     via `ErdGateway.getWorkspaces()` against the chosen instance.
 *   - **Record types**: multi-select dropdown, fetched per selected
 *     workspace via `ErdGateway.getWorkspaceMetadata(workspaceId)`,
 *     flattened across workspaces so the operator picks from one
 *     combined list (option labels include their parent workspace
 *     so two record types named "Project" stay distinguishable).
 *   - **Run name**: pre-populated from the current selection;
 *     editable. The auto-generated label rolls over whenever the
 *     selection changes IF the operator hasn't edited it manually
 *     (we track an `autoName` flag).
 *
 * Engine contract: at least one of `workspaceIds` / `recordTypeIds`
 * must be non-empty; the Trigger button stays disabled otherwise.
 */
interface CreateBackupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTriggered?: () => void;
}

const NAME_MAX = 50;

export function CreateBackupDialog({
  open,
  onOpenChange,
  onTriggered,
}: CreateBackupDialogProps): JSX.Element {
  const { trigger, isPending, error } = useTriggerPlanningBackup();
  const { api } = useAuth();
  const { details } = useLicense();

  // Build the list of instance choices from the license. The
  // primary always sits first; secondary configs append in their
  // license-rank order.
  const instanceOptions = useMemo<
    ReadonlyArray<{ value: string; label: string; ref: string }>
  >(() => {
    if (!details) return [];
    const out: Array<{ value: string; label: string; ref: string }> = [];
    if (details.primaryWorkfrontConfig) {
      out.push({
        value: details.primaryWorkfrontConfig.name,
        label: details.primaryWorkfrontConfig.name,
        ref: 'primary',
      });
    }
    for (const sec of details.secondaryWorkfrontConfigs) {
      out.push({ value: sec.name, label: sec.name, ref: sec.ref });
    }
    return out;
  }, [details]);

  // Selected instance name (matches `workfront_configs.name`; the
  // pipeline accepts either name or UUID since #157). Defaults to
  // the primary if available, otherwise the first option, otherwise
  // a placeholder.
  const [instance, setInstance] = useState('');
  useEffect(() => {
    if (!open) return;
    if (instance) return;
    setInstance(instanceOptions[0]?.value ?? '');
  }, [open, instance, instanceOptions]);

  // `workspaceRef` is what the engine's `resolveWorkspace` accepts
  // (`primary` / `secondary:N` / a direct UUID). The Planning proxy
  // wants this ref, not the human-readable name. Look it up off the
  // currently-chosen instance name.
  const workspaceRef = instanceOptions.find((o) => o.value === instance)?.ref ?? 'primary';

  // Fetch the Planning workspaces for the chosen instance.
  const workspacesQuery = useQuery<PlanningWorkspace[], Error>({
    queryKey: ['planning', 'workspaces', workspaceRef, open],
    enabled: open && instance.length > 0,
    queryFn: async () => {
      const gateway = new ErdGateway(api, workspaceRef);
      return gateway.getWorkspaces();
    },
    staleTime: 60_000,
  });

  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  // Reset workspace + record-type selection when the instance or
  // dialog visibility changes — different instances expose different
  // sets, so the old selection is meaningless.
  useEffect(() => {
    setSelectedWorkspaceIds(new Set());
    setSelectedRecordTypeIds(new Set());
  }, [instance]);
  useEffect(() => {
    if (!open) {
      setSelectedWorkspaceIds(new Set());
      setSelectedRecordTypeIds(new Set());
      setName('');
      setAutoName(true);
    }
  }, [open]);

  // For each selected workspace fan out a metadata query — the
  // result is `{ recordTypes, sections }`. We flatten across all
  // selected workspaces into one option list for the record-type
  // dropdown.
  const metadataQueries = useQueries({
    queries: Array.from(selectedWorkspaceIds).map((wsId) => ({
      queryKey: ['planning', 'workspace-metadata', workspaceRef, wsId],
      enabled: open,
      queryFn: async () => {
        const gateway = new ErdGateway(api, workspaceRef);
        return { wsId, metadata: await gateway.getWorkspaceMetadata(wsId) };
      },
      staleTime: 60_000,
    })),
  });
  const recordTypeOptions = useMemo<
    ReadonlyArray<{ id: string; label: string; workspaceName: string }>
  >(() => {
    const out: Array<{ id: string; label: string; workspaceName: string }> = [];
    const workspaceNameById = new Map(
      (workspacesQuery.data ?? []).map((w) => [w.id, w.name] as const),
    );
    for (const q of metadataQueries) {
      const value = q.data;
      if (!value) continue;
      const wsName = workspaceNameById.get(value.wsId) ?? value.wsId;
      for (const rt of value.metadata.recordTypes) {
        out.push({ id: rt.id, label: rt.name, workspaceName: wsName });
      }
    }
    out.sort(
      (a, b) => a.workspaceName.localeCompare(b.workspaceName) || a.label.localeCompare(b.label),
    );
    return out;
  }, [metadataQueries, workspacesQuery.data]);

  const [selectedRecordTypeIds, setSelectedRecordTypeIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  // Drop record-type ids whose workspace is no longer selected.
  useEffect(() => {
    const validIds = new Set(recordTypeOptions.map((o) => o.id));
    setSelectedRecordTypeIds((prev) => {
      let mutated = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (validIds.has(id)) next.add(id);
        else mutated = true;
      }
      return mutated ? next : prev;
    });
  }, [recordTypeOptions]);

  // Auto-name: rolls over with the current selection. `autoName`
  // flips to false the first time the operator types in the field;
  // after that the field stays as-is regardless of what's selected.
  const [name, setName] = useState('');
  const [autoName, setAutoName] = useState(true);

  const generatedName = useMemo(() => {
    if (!instance) return '';
    const workspaceNames = (workspacesQuery.data ?? [])
      .filter((w) => selectedWorkspaceIds.has(w.id))
      .map((w) => w.name);
    const recordTypeNames = recordTypeOptions
      .filter((o) => selectedRecordTypeIds.has(o.id))
      .map((o) => o.label);
    const parts: string[] = [instance];
    if (workspaceNames.length === 1) parts.push(workspaceNames[0]!);
    else if (workspaceNames.length > 1) parts.push(`${workspaceNames.length} workspaces`);
    if (recordTypeNames.length === 1) parts.push(recordTypeNames[0]!);
    else if (recordTypeNames.length > 1) parts.push(`${recordTypeNames.length} record types`);
    // ASCII hyphen — the engine's name regex
    // (`/^[\x20-\x7E]*$/` in `apps/audit-engine/src/handlers/
    // producers.ts`) rejects non-ASCII chars like an em-dash.
    return parts.join(' - ').slice(0, NAME_MAX);
  }, [
    instance,
    selectedWorkspaceIds,
    selectedRecordTypeIds,
    workspacesQuery.data,
    recordTypeOptions,
  ]);

  useEffect(() => {
    if (autoName) setName(generatedName);
  }, [autoName, generatedName]);

  const toggleSet = <T,>(setter: (next: Set<T>) => void, prev: ReadonlySet<T>, value: T): void => {
    const next = new Set(prev);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setter(next);
  };

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (selectedWorkspaceIds.size === 0 && selectedRecordTypeIds.size === 0) return;
    await trigger({
      instance: instance.trim(),
      ...(selectedWorkspaceIds.size > 0 ? { workspaceIds: Array.from(selectedWorkspaceIds) } : {}),
      ...(selectedRecordTypeIds.size > 0
        ? { recordTypeIds: Array.from(selectedRecordTypeIds) }
        : {}),
      ...(name.trim() ? { name: name.trim() } : {}),
    });
    onOpenChange(false);
    onTriggered?.();
  };

  const submitDisabled =
    !instance.trim() || (selectedWorkspaceIds.size === 0 && selectedRecordTypeIds.size === 0);

  const workspaceTriggerLabel = (() => {
    const total = workspacesQuery.data?.length ?? 0;
    const selected = selectedWorkspaceIds.size;
    if (workspacesQuery.isLoading) return 'Loading…';
    if (total === 0) return 'No workspaces';
    if (selected === 0) return 'Select workspaces';
    if (selected === 1) {
      const w = (workspacesQuery.data ?? []).find((x) => selectedWorkspaceIds.has(x.id));
      return w?.name ?? '1 workspace';
    }
    return `${selected} workspaces`;
  })();

  const recordTypeTriggerLabel = (() => {
    const total = recordTypeOptions.length;
    const selected = selectedRecordTypeIds.size;
    const anyLoading = metadataQueries.some((q) => q.isLoading);
    if (anyLoading && total === 0) return 'Loading…';
    if (selectedWorkspaceIds.size === 0) return 'Select a workspace first';
    if (total === 0) return 'No record types';
    if (selected === 0) return 'All record types in workspace';
    if (selected === 1) {
      return (
        recordTypeOptions.find((o) => selectedRecordTypeIds.has(o.id))?.label ?? '1 record type'
      );
    }
    return `${selected} record types`;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-foreground/15 bg-[color:var(--surface-overlay)] text-foreground">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <HardDrive className="h-4 w-4" /> Trigger Planning backup
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <section>
            <Label className="mb-1 block text-sm text-foreground/80" htmlFor="backup-instance">
              Planning instance
            </Label>
            <Select value={instance} onValueChange={setInstance}>
              <SelectTrigger
                id="backup-instance"
                className="border-foreground/15 bg-foreground/[0.04] text-foreground"
              >
                <SelectValue placeholder="Pick an instance" />
              </SelectTrigger>
              {/* z-[10001] so the popover floats above the dialog
                  (dialog content is z-[10000]). */}
              <SelectContent className="z-[10001]">
                {instanceOptions.length === 0 ? (
                  <SelectItem value="__none__" disabled>
                    No workfront-config on this license
                  </SelectItem>
                ) : (
                  instanceOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-foreground/40">
              Sourced from the license's primary + secondary workfront-configs.
            </p>
          </section>

          <section>
            <Label className="mb-1 block text-sm text-foreground/80">Workspaces</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    'inline-flex w-full items-center justify-between rounded-md border border-foreground/15 bg-foreground/[0.04] px-3 py-2 text-sm text-foreground/80 transition-colors hover:border-foreground/30',
                    workspacesQuery.isLoading && 'opacity-60',
                  )}
                  disabled={workspacesQuery.isLoading}
                >
                  <span className="flex items-center gap-2">
                    {workspacesQuery.isLoading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-foreground/50" />
                    ) : null}
                    {workspaceTriggerLabel}
                  </span>
                  <ChevronDown className="h-4 w-4 text-foreground/50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="z-[10001] max-h-[40vh] w-[--radix-dropdown-menu-trigger-width] overflow-y-auto"
              >
                <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(workspacesQuery.data ?? []).length === 0 ? (
                  <p className="px-2 py-1.5 text-xs text-foreground/40">No workspaces.</p>
                ) : (
                  (workspacesQuery.data ?? []).map((w) => (
                    <DropdownMenuCheckboxItem
                      key={w.id}
                      checked={selectedWorkspaceIds.has(w.id)}
                      onCheckedChange={() =>
                        toggleSet(setSelectedWorkspaceIds, selectedWorkspaceIds, w.id)
                      }
                    >
                      {w.name}
                    </DropdownMenuCheckboxItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </section>

          <section>
            <Label className="mb-1 block text-sm text-foreground/80">Record types</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex w-full items-center justify-between rounded-md border border-foreground/15 bg-foreground/[0.04] px-3 py-2 text-sm text-foreground/80 transition-colors hover:border-foreground/30 disabled:opacity-60"
                  disabled={selectedWorkspaceIds.size === 0}
                >
                  <span className="flex items-center gap-2">
                    {metadataQueries.some((q) => q.isLoading) ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-foreground/50" />
                    ) : null}
                    {recordTypeTriggerLabel}
                  </span>
                  <ChevronDown className="h-4 w-4 text-foreground/50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="z-[10001] max-h-[40vh] w-[--radix-dropdown-menu-trigger-width] overflow-y-auto"
              >
                <DropdownMenuLabel>Record types</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {recordTypeOptions.length === 0 ? (
                  <p className="px-2 py-1.5 text-xs text-foreground/40">
                    {selectedWorkspaceIds.size === 0
                      ? 'Pick a workspace to load record types.'
                      : 'No record types in the selected workspace(s).'}
                  </p>
                ) : (
                  recordTypeOptions.map((opt) => (
                    <DropdownMenuCheckboxItem
                      key={opt.id}
                      checked={selectedRecordTypeIds.has(opt.id)}
                      onCheckedChange={() =>
                        toggleSet(setSelectedRecordTypeIds, selectedRecordTypeIds, opt.id)
                      }
                    >
                      <span className="flex w-full items-center justify-between gap-2">
                        <span className="truncate">{opt.label}</span>
                        <span className="shrink-0 text-[0.65rem] uppercase tracking-wider text-foreground/40">
                          {opt.workspaceName}
                        </span>
                      </span>
                    </DropdownMenuCheckboxItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <p className="mt-1 text-xs text-foreground/40">
              At least one workspace or record type is required.
            </p>
          </section>

          <section>
            <Label className="mb-1 block text-sm text-foreground/80" htmlFor="backup-name">
              Run name (optional)
            </Label>
            <Input
              id="backup-name"
              value={name}
              onChange={(e) => {
                setAutoName(false);
                setName(e.target.value);
              }}
              placeholder={generatedName || 'Pre-merge snapshot'}
              maxLength={NAME_MAX}
              className="border-foreground/15 bg-foreground/[0.04] text-foreground placeholder:text-foreground/30"
            />
            <p className="mt-1 flex items-center gap-1.5 text-xs text-foreground/40">
              {autoName ? (
                <span className="inline-flex items-center gap-1">
                  <Check className="h-3 w-3" /> Auto-generated from selection
                </span>
              ) : (
                <button
                  type="button"
                  className="text-foreground/60 hover:underline"
                  onClick={() => {
                    setAutoName(true);
                    setName(generatedName);
                  }}
                >
                  Reset to auto
                </button>
              )}
            </p>
          </section>

          {error ? (
            <div className="rounded-md border border-rose-400/30 bg-rose-500/10 p-2 text-xs text-rose-800 dark:text-rose-200">
              {error.message}
            </div>
          ) : null}

          <DialogFooter className="flex flex-row justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-foreground/15 text-foreground/70"
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitDisabled || isPending}
              className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
            >
              {isPending ? 'Triggering…' : 'Trigger backup'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
